import {
  differenceInCalendarDays,
  endOfYear,
  format,
  isSameDay,
  isValid,
  parse,
  startOfDay,
  startOfYear,
  subDays,
  subYears,
} from "date-fns";

export interface SalesDateRange {
  start: Date;
  end: Date;
}

export const DEFAULT_RANGE_WEEKS = 12;

// Sales history lower bound when publication date is missing or pre-2010.
export const SALES_DATA_EPOCH = new Date(2010, 0, 1);

export const isValidSalesDateRange = ({
  start,
  end,
}: SalesDateRange): boolean => start <= end;

const normalizeReleaseDate = (releaseDate?: Date | null): Date | undefined =>
  releaseDate && isValid(releaseDate) ? startOfDay(releaseDate) : undefined;

export const lifetimeStartDate = (releaseDate?: Date | null): Date => {
  const release = normalizeReleaseDate(releaseDate);
  if (!release) {
    return new Date(SALES_DATA_EPOCH);
  }
  const epoch = startOfDay(SALES_DATA_EPOCH);
  return release > epoch ? release : epoch;
};

export const clampRangeToReleaseDate = (
  range: SalesDateRange,
  releaseDate?: Date | null,
): SalesDateRange => {
  const earliest = lifetimeStartDate(releaseDate);
  const start = range.start < earliest ? earliest : range.start;
  return { start, end: range.end };
};

export const defaultSalesDateRange = (
  referenceDate: Date = new Date(),
  releaseDate?: Date | null,
): SalesDateRange =>
  clampRangeToReleaseDate(
    {
      end: startOfDay(referenceDate),
      // 12-week inclusive window: 84 days with both endpoints, so subtract one fewer day.
      start: subDays(startOfDay(referenceDate), DEFAULT_RANGE_WEEKS * 7 - 1),
    },
    releaseDate,
  );

export const currentYearRange = (
  referenceDate: Date = new Date(),
  releaseDate?: Date | null,
): SalesDateRange => {
  const end = startOfDay(referenceDate);
  return clampRangeToReleaseDate({ start: startOfYear(end), end }, releaseDate);
};

export const lastYearRange = (
  referenceDate: Date = new Date(),
  releaseDate?: Date | null,
): SalesDateRange => {
  const lastYear = subYears(startOfDay(referenceDate), 1);
  return clampRangeToReleaseDate(
    { start: startOfYear(lastYear), end: endOfYear(lastYear) },
    releaseDate,
  );
};

export const lifetimeRange = (
  referenceDate: Date = new Date(),
  releaseDate?: Date | null,
): SalesDateRange =>
  clampRangeToReleaseDate(
    { start: lifetimeStartDate(releaseDate), end: startOfDay(referenceDate) },
    releaseDate,
  );

export const rangeSpanDays = ({ start, end }: SalesDateRange): number =>
  differenceInCalendarDays(end, start) + 1;

export const rangesMatch = (a: SalesDateRange, b: SalesDateRange): boolean =>
  isSameDay(a.start, b.start) && isSameDay(a.end, b.end);

export type SalesDatePresetId =
  | "last-12-weeks"
  | "current-year"
  | "last-year"
  | "lifetime";

// Preset start was clamped to earliestSelectableDate.
export const isStartClamped = (
  range: SalesDateRange,
  presetUnclampedStart: Date | null | undefined,
  earliestSelectableDate: Date | null | undefined,
): boolean => {
  if (!presetUnclampedStart || !earliestSelectableDate) {
    return false;
  }
  const bound = startOfDay(earliestSelectableDate);
  return (
    isSameDay(range.start, bound) && startOfDay(presetUnclampedStart) < bound
  );
};

export const toIsoDate = (date: Date): string => format(date, "yyyy-MM-dd");

export const parseIsoDate = (value?: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsed) ? startOfDay(parsed) : undefined;
};

// Earliest sales day across sources; can predate publication for pre-order titles.
export const earliestDefined = (
  ...dates: (Date | undefined)[]
): Date | undefined =>
  dates.reduce<Date | undefined>((earliest, date) => {
    if (!date) {
      return earliest;
    }
    return !earliest || date < earliest ? date : earliest;
  }, undefined);

export const formatSalesDateRange = ({ start, end }: SalesDateRange): string =>
  `${format(start, "d MMM yyyy")} – ${format(end, "d MMM yyyy")}`;

const INPUT_DATE_FORMAT = "dd.MM.yyyy";

export const formatInputDate = (date: Date): string =>
  format(date, INPUT_DATE_FORMAT);

export const parseInputDate = (text: string): Date | undefined => {
  const parsed = parse(text, INPUT_DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : undefined;
};
